import { useState, FunctionComponent } from "react";
import { RatingComponent } from "./RatingComponent";
import { TagInput } from "./TagInput";
import { Button } from "./Button";
import { TrashIcon } from "./icons";
import { DuplicatesLink } from "./DuplicatesLink";

export type ItemFormValues = {
  name: string;
  tags: string[];
  rating: number | null;
};

export type ItemFormProps = {
  tagsAutocomplete: string[];
  /** Absent when adding; the item's current values when editing. */
  initial?: ItemFormValues;
  submitLabel?: string;
  onSave: (data: ItemFormValues) => void;
  /** Only supplied when editing — it is the one thing the two modes differ by. */
  onDelete?: () => void;
  /**
   * Offers "Possible duplicates" under the name. Only when adding: editing an item
   * would otherwise report the item itself as a duplicate of itself.
   */
  checkDuplicates?: boolean;
};

const EMPTY: ItemFormValues = { name: "", tags: [], rating: null };

/**
 * The one form for adding and for editing.
 *
 * Editing used to be a strip along the top of the results pane that wrote through on
 * every keystroke — a PUT per character. Here the fields are held locally and sent
 * once, so a half-typed name never reaches the server.
 */
export const ItemForm: FunctionComponent<ItemFormProps> = ({
  tagsAutocomplete,
  initial,
  submitLabel = "Add",
  onSave,
  onDelete,
  checkDuplicates = false,
}) => {
  const [values, setValues] = useState<ItemFormValues>(initial ?? EMPTY);

  const update = (patch: Partial<ItemFormValues>) =>
    setValues((prev) => ({ ...prev, ...patch }));

  const handleSubmit = (e: { preventDefault?: () => unknown }) => {
    e.preventDefault?.();
    if (!values.name.trim()) return;
    onSave({ ...values, name: values.name.trim() });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {/* The link is a sibling of the label rather than inside it: a label forwards a
          click anywhere in it to its control, which would swallow the button. */}
      <div className="flex flex-col gap-1">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink">Name</span>
          <input
            value={values.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Hokusai"
            autoFocus
            className="h-10 rounded-md border border-line-strong bg-surface px-3 text-ink
              placeholder:text-ink-faint focus:border-accent focus:outline-1 focus:outline-accent"
          />
        </label>
        {checkDuplicates ? <DuplicatesLink name={values.name} /> : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink">Tags</span>
        <TagInput
          items={values.tags}
          autocomplete={tagsAutocomplete}
          onChange={(tags) => update({ tags })}
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">Rating</span>
        <RatingComponent
          score={values.rating}
          onChange={(rating) => update({ rating })}
        />
      </div>

      {/* Delete sits apart from Save, on the other end of the row, so the destructive
          one is never where the confirming one was a moment ago. */}
      <div className="flex items-center gap-2 border-t border-line pt-3">
        {onDelete ? (
          <Button variant="danger" size="md" onClick={onDelete}>
            <TrashIcon />
            Delete
          </Button>
        ) : null}
        <div className="flex-1" />
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={!values.name.trim()}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
